import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = 'fp_tenant_brand';
const TenantBrandContext = createContext(null);

const clean = (hex, fallback) => (/^#[0-9a-fA-F]{6}$/.test(hex || '') ? hex : fallback);

/** Perceived brightness, so text on a brand colour is never a guess. */
const luminance = (hex) => {
  const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255)
    .map(c => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
const on = (hex) => (luminance(hex) > 0.45 ? '#0f172a' : '#ffffff');
const mix = (hex, white, amount) => {
  const p = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
  const t = white ? 255 : 0;
  return '#' + p.map(c => Math.round(c + (t - c) * amount).toString(16).padStart(2, '0')).join('');
};

/** Everything the branded front door needs, derived from three colours out of their logo. */
export const paletteFrom = (branding = {}) => {
  const primary = clean(branding.primary_color, '#0891b2');
  const secondary = clean(branding.secondary_color, '#a78bfa');
  const accent = clean(branding.accent_color, secondary);
  // A dark brand colour keeps its own ink; a pale gold gets darkened so headings stay readable.
  const ink = luminance(primary) > 0.55 ? mix(primary, false, 0.55) : primary;
  return {
    primary, secondary, accent, ink,
    onPrimary: on(primary),
    onSecondary: on(secondary),
    onAccent: on(accent),
    primaryHover: mix(primary, luminance(primary) > 0.5, 0.12),
    primarySoft: mix(primary, true, 0.9),
    secondarySoft: mix(secondary, true, 0.88),
    accentSoft: mix(accent, true, 0.85),
    hairline: mix(primary, true, 0.86),
  };
};

export const TenantBrandProvider = ({ children }) => {
  const [tenant, setTenant] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(KEY) || 'null'); } catch { return null; }
  });

  const remember = useCallback((payload) => {
    setTenant(payload);
    if (payload) sessionStorage.setItem(KEY, JSON.stringify(payload));
    else sessionStorage.removeItem(KEY);
  }, []);

  const loadBySlug = useCallback(async (slug) => {
    const r = await axios.get(`${API}/orgs/by-slug/${slug}/public`);
    const payload = { slug, name: r.data.organization.name, branding: r.data.branding,
                      organization: r.data.organization, programs: r.data.programs || [] };
    remember(payload);
    return payload;
  }, [remember]);

  const palette = useMemo(() => paletteFrom(tenant?.branding), [tenant]);

  // The tenant's colours as CSS variables, so any page can wear their brand without prop-drilling.
  useEffect(() => {
    const root = document.documentElement;
    const vars = {
      '--tenant-primary': palette.primary, '--tenant-primary-foreground': palette.onPrimary,
      '--tenant-primary-hover': palette.primaryHover, '--tenant-primary-soft': palette.primarySoft,
      '--tenant-secondary': palette.secondary, '--tenant-accent': palette.accent,
      '--tenant-ink': palette.ink, '--tenant-hairline': palette.hairline,
    };
    Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
  }, [palette]);

  return (
    <TenantBrandContext.Provider value={{ tenant, palette, loadBySlug, remember,
                                          clearTenant: () => remember(null) }}>
      {children}
    </TenantBrandContext.Provider>
  );
};

export const useTenantBrand = () => useContext(TenantBrandContext) || {
  tenant: null, palette: paletteFrom({}), loadBySlug: async () => {}, remember: () => {},
  clearTenant: () => {},
};

/** The logo, framed so any shape sits well — a shield, a wordmark, a transparent PNG. */
export const LogoBox = ({ src, name, size = 'md', tone = 'light', className = '' }) => {
  const sizes = { sm: 'h-10 px-3', md: 'h-16 px-5', lg: 'h-28 px-7' };
  const light = tone === 'light';
  return (
    <div data-testid="tenant-logo-box"
      className={`inline-flex items-center justify-center rounded-3xl ${sizes[size]} ${className} ${light
        ? 'bg-white border border-slate-100 shadow-[0_8px_32px_rgba(15,23,42,0.08)]'
        : 'bg-white/10 backdrop-blur-md border border-white/20'}`}>
      {src
        ? <img src={src} alt={name} className="max-h-full max-w-[220px] object-contain" />
        : <span className={`font-outfit font-semibold ${light ? 'text-slate-900' : 'text-white'}`}>
            {name}
          </span>}
    </div>
  );
};

export default TenantBrandContext;
